/**
 * GENERATED_ASSET — game adapter for the adopted static Hush kit (sol61 batch001; mirrors tidalCascade/adapter.ts).
 *
 * HS-01 cache identity is content-bound: the logical recipe hash (recipe + resolved palette roles + style/detail/motif
 *       tokens + token revision + generator revision; never the SVG namespace) plus adapter revision and template frame.
 * HS-02 SVG definition ids are scoped `hs_<template>_<12-hex logical digest>`, so different palettes or recipes never
 *       share a definition id, and identical content resolves to identical markup.
 * HS-03 the kit's source art (nonzero viewBox origin, bottom-center support at (0,0)) is untouched. The template-frame
 *       projection uniformly scales it and translates so the PAINTED support contact (the `support` anchor: the
 *       lantern's wooden foot, the reed roots) lands exactly on the frame's bottom-center (w/2, h), which is what the
 *       current prop owner places at prop.y. The scale is the largest that keeps every painted pixel above the
 *       contact inside the frame. Ground paint BELOW the contact is limited to the lantern's standing contact shadow
 *       (ellipse cy=2 ry=3 at 18% opacity): its lower sliver (<= 5 design units) falls under the floor line and is
 *       clipped by the texture frame rather than lifting the foot off the ground.
 * HS-04 HANGING art (the hanging lantern, the ribbon) has no ground contact: it is fitted uniformly into its frame,
 *       centered horizontally and top-aligned, and reports its attachment points in frame coordinates — `hangPoint`
 *       (lantern `hang`, ribbon `tie`) and `hookPoints` (the lantern's painted `ribbon_tie`). The free-standing
 *       `lantern_brace` (brace.ts) is drawn in frame coordinates and reports its iron hook seat as a hook point.
 *
 * No placement, collision, trigger or gameplay geometry is produced. Generation alone does not claim map or human acceptance.
 */
import {
  HUSH_REEDS_CONCEALED, HUSH_RIBBON_REST, HUSH_REEDS_RETURN_OPEN, HUSH_RENDER_TOKENS, REVISION,
  buildStaticAsset, canonical,
} from '../../../../../shared/assets/hushStatic';
import type {
  DraftArtifact, PaletteRole, ProtectedVisualRegion, ResolvedPalette, StaticRecipe, Tokens, VisualAnchor, VisualBounds,
} from '../../../../../shared/assets/hushStatic';
import { HUSH_PROP_TEMPLATES } from '../../../../../shared/scene/hushPropTemplates';
import { TOWN_PROP_PALETTE_KEYS, type TownPropPalette } from '../../../../../shared/scene/townPropTemplates';
import { HUSH_LANTERN_BRACE, buildLanternBrace, type BraceRecipe } from './brace';
import { mix } from '../tidalCascade/palette';
import { sha256, stableStringify } from '../tidalCascade/rng';

export const HUSH_STATIC_ADAPTER_REVISION = 'hush-static.adapter/revision-01';

/**
 * Templates backed by a kit recipe. The two lantern templates moved to lantern v2 (`../hushLantern`): the v1 kit
 * lantern read as a face beside the ghosts. Its art (`HUSH_LANTERN_SALT_WORN*` in the shared kit) is kept as the
 * scarecrow-head starting point (hideout plan P6).
 */
export type HushKitTemplate = 'crooked_ribbon' | 'reed_curtain' | 'reed_curtain_open';
export type HushStaticTemplate = HushKitTemplate | 'lantern_brace';

/** Template -> recipe. */
export const HUSH_STATIC_RECIPES: Readonly<Record<HushKitTemplate, StaticRecipe>> = {
  crooked_ribbon: HUSH_RIBBON_REST,
  reed_curtain: HUSH_REEDS_CONCEALED,
  reed_curtain_open: HUSH_REEDS_RETURN_OPEN,
};

/** Non-recipe bindings (primitive-kit compositions). Mutable shape only so tests can prove digest sensitivity. */
export const HUSH_STATIC_BINDINGS: { lantern_brace: BraceRecipe } = { lantern_brace: HUSH_LANTERN_BRACE };

/** How kit art enters its frame: ground contact to bottom-center (HS-03) or hanging fit (HS-04). */
const PROJECTION: Readonly<Record<HushKitTemplate, 'support-contact-to-frame-bottom' | 'hanging-fit-top-center'>> = {
  crooked_ribbon: 'hanging-fit-top-center',
  reed_curtain: 'support-contact-to-frame-bottom',
  reed_curtain_open: 'support-contact-to-frame-bottom',
};

const has = (o: object, k: unknown): boolean => typeof k === 'string' && Object.prototype.hasOwnProperty.call(o, k);
export function isHushStaticTemplate(t: unknown): t is HushStaticTemplate {
  return has(HUSH_STATIC_RECIPES, t) || has(HUSH_STATIC_BINDINGS, t);
}

function checkTemplate(template: unknown): asserts template is HushStaticTemplate {
  if (!isHushStaticTemplate(template)) {
    const known = [...Object.keys(HUSH_STATIC_RECIPES), ...Object.keys(HUSH_STATIC_BINDINGS)].join(', ');
    throw new Error(`Unknown hush static template ${JSON.stringify(template)}. Known: ${known}`);
  }
}

function recipeFor(template: unknown): StaticRecipe {
  checkTemplate(template);
  if (template === 'lantern_brace') throw new Error('hush static lantern_brace has no kit recipe');
  return HUSH_STATIC_RECIPES[template];
}

export interface TemplateFrame { readonly w: number; readonly h: number }
export function hushStaticFrame(template: HushStaticTemplate): TemplateFrame {
  return HUSH_PROP_TEMPLATES[template].frame;
}

// ---------------------------------------------------------------- palette -> roles ----------------------------------
const hex = (v: number): string => '#' + v.toString(16).padStart(6, '0');

function checkPalette(p: unknown): asserts p is TownPropPalette {
  if (!p || typeof p !== 'object') throw new Error('hush static palette: expected an object of 10 RGB integers');
  const r = p as Record<string, unknown>;
  for (const k of TOWN_PROP_PALETTE_KEYS) {
    const v = r[k];
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > 0xffffff) {
      throw new Error(`hush static palette.${k}: must be an integer RGB color 0..0xffffff (got ${String(v)})`);
    }
  }
}

/** Town key -> render-token role, exactly as HUSH_PROP_PALETTES encodes the token palettes. */
const TOWN_ROLE: Readonly<Record<(typeof TOWN_PROP_PALETTE_KEYS)[number], PaletteRole>> = {
  primary: 'paperBase', shade: 'paperShadow', roof: 'clothBlue', roofEdge: 'clothBlueLight', wood: 'woodBase',
  woodEdge: 'woodLight', stone: 'stoneBase', iron: 'outline', glass: 'warmCore', glassCore: 'paperLight',
};

function matchesTokenPalette(p: TownPropPalette, t: ResolvedPalette): boolean {
  return TOWN_PROP_PALETTE_KEYS.every((k) => hex(p[k]) === t[TOWN_ROLE[k]].toLowerCase());
}

/**
 * Resolve the 10-key town palette to the kit's 22 roles. A palette that equals a token palette's mapped keys
 * (recipe's own palette first, e.g. HUSH_PROP_PALETTES.coastal_salt / nook_warm) returns that exact token palette,
 * so default instances match the reviewed kit art byte-for-byte. Otherwise the mapped roles are replaced and derived
 * shades mix existing town colors (same derivation as tidalCascade); reed/moss/salt/water roles keep the recipe's
 * token values because the town palette has no such keys.
 */
export function hushStaticRoles(recipe: Pick<StaticRecipe, 'paletteId'>, p: TownPropPalette): ResolvedPalette {
  checkPalette(p);
  const palettes = HUSH_RENDER_TOKENS.palettes;
  const order = [recipe.paletteId, ...Object.keys(palettes).filter((id) => id !== recipe.paletteId)];
  for (const id of order) if (matchesTokenPalette(p, palettes[id])) return palettes[id];
  const t = palettes[recipe.paletteId];
  const [stone, iron, wood, roof] = [hex(p.stone), hex(p.iron), hex(p.wood), hex(p.roof)];
  return {
    ...t,
    outline: iron, outlineSoft: mix(iron, stone, 0.5),
    paperBase: hex(p.primary), paperShadow: hex(p.shade), paperLight: hex(p.glassCore), warmCore: hex(p.glass),
    woodBase: wood, woodLight: hex(p.woodEdge), woodShadow: mix(wood, iron, 0.4),
    clothBlue: roof, clothBlueLight: hex(p.roofEdge), clothBlueShadow: mix(roof, iron, 0.35),
    stoneBase: stone, stoneLight: mix(stone, hex(p.glassCore), 0.35), stoneShadow: mix(stone, iron, 0.35),
  };
}

// ---------------------------------------------------------------- artifacts (content-memoized) ---------------------
interface ResolvedArtifact { readonly art: DraftArtifact; readonly logicalRecipeHash: string; readonly scope: string }
const memo = new Map<string, ResolvedArtifact>();
const MEMO_LIMIT = 48;

function resolveArtifact(template: HushKitTemplate, palette: TownPropPalette): ResolvedArtifact {
  const recipe = recipeFor(template);
  const roles = hushStaticRoles(recipe, palette);
  const tokens: Tokens = { ...HUSH_RENDER_TOKENS, palettes: { ...HUSH_RENDER_TOKENS.palettes, [recipe.paletteId]: roles } };
  const logicalRecipeHash = sha256(canonical({
    recipe, palette: roles, style: tokens.style, detailTiers: tokens.detailTiers, masterMotif: tokens.masterMotif,
    tokenRevision: tokens.revision, generatorRevision: REVISION,
  }));
  const hit = memo.get(logicalRecipeHash + template);
  if (hit) return hit;
  const scope = `hs_${template}_${logicalRecipeHash.slice(0, 12)}`;
  const resolved = { art: buildStaticAsset(recipe, tokens, scope), logicalRecipeHash, scope };
  if (memo.size >= MEMO_LIMIT) memo.delete(memo.keys().next().value as string);
  memo.set(logicalRecipeHash + template, resolved);
  return resolved;
}

// ---------------------------------------------------------------- HS-03 projection ----------------------------------
export type Matrix6 = readonly [number, number, number, number, number, number];

function supportPoint(art: DraftArtifact): { x: number; y: number } {
  const support = art.anchors.find((a) => a.name === 'support' && a.role === 'support') ?? art.anchors.find((a) => a.name === 'origin');
  if (!support) throw new Error(`hush static ${art.recipeId}: no support anchor`);
  return support.point;
}

/** Largest uniform scale keeping all paint above the contact inside the frame, contact -> (w/2, h). */
export function hushStaticProjection(art: DraftArtifact, frame: TemplateFrame): Matrix6 {
  const c = supportPoint(art);
  const pb = art.paintBounds;
  const left = c.x - pb.x, right = pb.x + pb.width - c.x, up = c.y - pb.y;
  if (!(left > 0 && right > 0 && up > 0)) throw new Error(`hush static ${art.recipeId}: support is not inside the painted art`);
  const s = Math.min(frame.w / 2 / left, frame.w / 2 / right, frame.h / up);
  return [s, 0, 0, s, frame.w / 2 - s * c.x, frame.h - s * c.y];
}

/** HS-04: largest uniform scale fitting all paint, centered horizontally and top-aligned. */
export function hushHangingProjection(art: DraftArtifact, frame: TemplateFrame): Matrix6 {
  const pb = art.paintBounds;
  const s = Math.min(frame.w / pb.width, frame.h / pb.height);
  return [s, 0, 0, s, (frame.w - s * pb.width) / 2 - s * pb.x, -s * pb.y];
}

const apply = (m: Matrix6, p: { x: number; y: number }) => ({ x: m[0] * p.x + m[4], y: m[3] * p.y + m[5] });
function mapBounds(m: Matrix6, b: VisualBounds): VisualBounds {
  const a = apply(m, b);
  return { x: a.x, y: a.y, width: b.width * m[0], height: b.height * m[3] };
}
const n6 = (v: number): string => {
  if (!Number.isFinite(v)) throw new Error('hush static projection: non-finite component');
  return String(Math.round(v * 1e6) / 1e6);
};

export interface HushStaticPlacement {
  readonly template: HushKitTemplate;
  readonly recipeId: string;
  readonly frame: TemplateFrame;
  readonly matrix: Matrix6;
  readonly body: string;
  readonly scope: string;
  readonly logicalRecipeHash: string;
  /** Anchors/bounds/regions in TEMPLATE-FRAME coordinates (same matrix as the paint). */
  readonly anchors: readonly VisualAnchor[];
  readonly contentBounds: VisualBounds;
  readonly paintBounds: VisualBounds;
  readonly protectedRegions: readonly ProtectedVisualRegion[];
  /** Painted support contact (ground art: exactly (w/2, h)) or attachment origin (hanging art), frame coordinates. */
  readonly contact: { readonly x: number; readonly y: number };
  readonly projection: (typeof PROJECTION)[HushKitTemplate];
  /** Hanging art only: the frame-local point that must meet a host hook point. */
  readonly hangPoint?: { readonly x: number; readonly y: number };
  /** Frame-local points a hanging child may hang from. */
  readonly hookPoints: readonly { readonly x: number; readonly y: number }[];
  readonly geometry: DraftArtifact['geometry'];
  readonly collisionClaim: 'none';
}

const anchorPoint = (art: DraftArtifact, name: string) => art.anchors.find((a) => a.name === name)?.point;

export function describeHushStatic(template: HushKitTemplate, palette: TownPropPalette): HushStaticPlacement {
  const { art, logicalRecipeHash, scope } = resolveArtifact(template, palette);
  const frame = hushStaticFrame(template);
  const projection = PROJECTION[template];
  const m = projection === 'hanging-fit-top-center' ? hushHangingProjection(art, frame) : hushStaticProjection(art, frame);
  const hang = projection === 'hanging-fit-top-center' ? anchorPoint(art, 'hang') ?? anchorPoint(art, 'tie') : undefined;
  // Reed layers (back stalks + middle, ties, foreground edge stalks) are already flattened into `art.body` in order.
  const body = `<g data-part="hush-static-${template.replace(/_/g, '-')}" data-recipe="${art.recipeId}" `
    + `data-integration="GENERATED_ASSET" transform="translate(${n6(m[4])} ${n6(m[5])}) scale(${n6(m[0])})">`
    + `<defs>${art.defs}</defs>${art.body}</g>`;
  return {
    template, recipeId: art.recipeId, frame, matrix: m, body, scope, logicalRecipeHash,
    anchors: art.anchors.map((a) => ({ ...a, point: apply(m, a.point) })),
    contentBounds: mapBounds(m, art.contentBounds),
    paintBounds: mapBounds(m, art.paintBounds),
    protectedRegions: art.protectedRegions.map((r) => ({ ...r, bounds: mapBounds(m, r.bounds) })),
    contact: apply(m, supportPoint(art)),
    projection,
    ...(hang ? { hangPoint: apply(m, hang) } : {}),
    hookPoints: [],
    geometry: art.geometry,
    collisionClaim: 'none',
  };
}

/** Drop-in body for buildTownTemplateBody: a <g> (with its own scoped <defs>) in the template's frame coordinates. */
export function buildHushStaticBody(template: HushStaticTemplate, palette: TownPropPalette): string {
  checkTemplate(template);
  if (template === 'lantern_brace') return describeLanternBrace(palette).body;
  return describeHushStatic(template, palette).body;
}

/** The free-standing brace in its frame, with its hook seat (a host hook point) and painted foot. */
export function describeLanternBrace(palette: TownPropPalette) {
  const recipe = HUSH_STATIC_BINDINGS.lantern_brace;
  const roles = hushStaticRoles({ paletteId: 'coastal_salt' }, palette);
  const frame = hushStaticFrame('lantern_brace');
  if (frame.w !== recipe.frame.w || frame.h !== recipe.frame.h) throw new Error('hush static lantern_brace: catalog frame drifted from recipe');
  const placed = buildLanternBrace(recipe, roles);
  const logicalRecipeHash = sha256(canonical({ recipe, palette: roles, adapter: HUSH_STATIC_ADAPTER_REVISION }));
  return { ...placed, frame, logicalRecipeHash, hookPoints: [placed.hook], collisionClaim: 'none' as const };
}

// ---------------------------------------------------------------- HS-01 cache identity ------------------------------
/**
 * `__tpl_<template>_<10 palette hexes>` (the canonical townTemplateTextureSuffix shape, computed inline so this
 * module never imports ../townPropTemplates, which will dispatch here) + '_' + 16-hex content digest.
 */
export function hushStaticTextureSuffix(template: HushStaticTemplate, palette: TownPropPalette): string {
  checkTemplate(template);
  const logicalRecipeHash = template === 'lantern_brace'
    ? describeLanternBrace(palette).logicalRecipeHash
    : resolveArtifact(template, palette).logicalRecipeHash;
  const colors = TOWN_PROP_PALETTE_KEYS.map((k) => palette[k].toString(16).padStart(6, '0')).join('-');
  const digest = sha256(stableStringify({
    template, logical: logicalRecipeHash, adapter: HUSH_STATIC_ADAPTER_REVISION, frame: hushStaticFrame(template),
    projection: template === 'lantern_brace' ? 'frame-drawn' : PROJECTION[template],
  })).slice(0, 16);
  return `__tpl_${template}_${colors}_${digest}`;
}
